import { SurveyBinding, SurveyFooterAction, SurveyOperation, SurveyPresentation, SurveyRequest } from './types';
import { controlActions, hasOnly, isIdentifier, isRecord, isRevision, utf8Size, validAnswer, validPresentation } from './validation';

const primarySurveyActions: SurveyFooterAction[] = ['continue', 'submitAnswer', 'selectRoute', 'decideConsent'];
const surveyFooterActionOrder: Partial<Record<SurveyFooterAction, number>> = {
  goBack: 0,
  requestHelp: 1,
  continue: 2,
  submitAnswer: 2,
  selectRoute: 2,
  decideConsent: 2,
  skipOnboardingSurvey: 4,
};

export const validSurveyRequest = (value: unknown): value is SurveyRequest => {
  if (!isRecord(value) || !hasOnly(value, ['sessionId', 'roomId', 'interactiveSurveySchemaVersion', 'interactiveSurvey']) ||
      !isIdentifier(value.sessionId) || !isIdentifier(value.roomId) || value.interactiveSurveySchemaVersion !== 1 || !isRecord(value.interactiveSurvey)) return false;
  const interaction = value.interactiveSurvey;
  if (!isIdentifier(interaction.formId) || !isRevision(interaction.presentationRevision) ||
      typeof interaction.interactionId !== 'string' || !/^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(interaction.interactionId)) return false;
  const keys = ['messageType', 'interactionId', 'formId', 'presentationRevision'];
  const valid = interaction.messageType === 'surveyAnswer' ? hasOnly(interaction, [...keys, 'answer']) && validAnswer(interaction.answer) :
    interaction.messageType === 'surveyAction' ? hasOnly(interaction, [...keys, 'action']) &&
      [...controlActions, 'requestHelp'].includes(String(interaction.action)) :
    interaction.messageType === 'governedSurveyHelp' && hasOnly(interaction, [...keys, 'text']) && isIdentifier(interaction.text);
  return valid && utf8Size(JSON.stringify(value)) <= 65536;
};

export const canUseSurveyAction = (form: SurveyPresentation, action: string) =>
  action !== 'useStandardWording' &&
  form.footerActions.some((footer) => footer.action === action && footer.enabled === true) &&
  (action !== 'requestHelp' || !!form.help?.available);

export const visibleSurveyFooterActions = (form: SurveyPresentation) => {
  const visible = form.footerActions.filter((footer) =>
    footer.action === 'requestHelp' || canUseSurveyAction(form, footer.action));
  return [...visible].sort((left, right) =>
    (surveyFooterActionOrder[left.action] ?? 3) - (surveyFooterActionOrder[right.action] ?? 3));
};

export const isPrimarySurveyAction = (action: SurveyFooterAction) => primarySurveyActions.includes(action);

export const canUseGovernedSurveyHelp = (form: SurveyPresentation) =>
  form.help?.mode === 'generative' && form.help.started === true && form.help.available && form.help.remaining > 0;

export const validOperation = (form: SurveyPresentation, operation: SurveyOperation): boolean => {
  if (!isRecord(operation)) return false;
  if (operation.messageType === 'surveyAction') return hasOnly(operation, ['messageType', 'action']) &&
    (controlActions.includes(operation.action) || operation.action === 'requestHelp' && !!form.help) &&
    canUseSurveyAction(form, operation.action);
  if (operation.messageType === 'governedSurveyHelp') return hasOnly(operation, ['messageType', 'text']) &&
    canUseGovernedSurveyHelp(form) && isIdentifier(operation.text) &&
    operation.text.length <= 5000;
  if (operation.messageType !== 'surveyAnswer' || !hasOnly(operation, ['messageType', 'answer']) || !validAnswer(operation.answer)) return false;
  const answer = operation.answer;
  const optionExists = (choiceId: string) => form.options.some((option) => option.id === choiceId);
  if (form.messageType === 'surveyRoute') return canUseSurveyAction(form, 'selectRoute') && answer.type === 'route' && optionExists(answer.choiceId);
  if (form.messageType === 'surveyConsent') return canUseSurveyAction(form, 'decideConsent') && answer.type === 'consent';
  if (form.messageType !== 'surveyQuestion' || !canUseSurveyAction(form, 'submitAnswer')) return false;
  if (answer.type === 'nonAnswer') return form.nonAnswerOptions.some((option) => option.outcome === answer.outcome);
  if (answer.type !== form.inputType) return false;
  if (answer.type === 'singleChoice') {
    const option = form.options.find((item) => item.id === answer.choiceId);
    return !!option && !!option.requiresFreeText === !!answer.specifyText;
  }
  if (answer.type === 'multiChoice') return answer.choiceIds.every(optionExists);
  return answer.type === 'freeText';
};

const freeze = <Value>(value: Value): Value => {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
  return value;
};

export const buildSurveyRequest = (
  binding: SurveyBinding,
  form: SurveyPresentation,
  operation: SurveyOperation,
  interactionId: string
): SurveyRequest => {
  if (!isIdentifier(binding.accountId) || !isIdentifier(binding.sessionId) || !isIdentifier(binding.roomId) ||
      !/^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(interactionId) ||
      !validPresentation(form) || !validOperation(form, operation)) throw new Error('invalid_contract');
  const request: SurveyRequest = {
    sessionId: binding.sessionId,
    roomId: binding.roomId,
    interactiveSurveySchemaVersion: 1,
    interactiveSurvey: {
      ...operation,
      interactionId,
      formId: form.formId,
      presentationRevision: form.presentationRevision,
    },
  };
  const serialized = JSON.stringify(request);
  if (utf8Size(serialized) > 65536) throw new Error('invalid_contract');
  return freeze(JSON.parse(serialized) as SurveyRequest);
};
